/**
 * Case Field Options tab - manage dropdown options for Status, Case Reason, Priority, Origin (Settings → Case Module)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/api';

const FIELD_CONFIG: { key: string; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'case_reason', label: 'Case Reason' },
  { key: 'priority', label: 'Priority' },
  { key: 'case_origin', label: 'Origin' },
];

const CHIP_COLORS: { value: string; label: string }[] = [
  { value: 'default', label: 'Default (grey)' },
  { value: 'primary', label: 'Primary' },
  { value: 'success', label: 'Success (green)' },
  { value: 'warning', label: 'Warning (amber)' },
  { value: 'error', label: 'Error (red)' },
];

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';

interface OptionRow {
  id: string;
  field_name: string;
  value: string;
  label: string;
  sort_order: number;
  color?: string | null;
}

interface CaseFieldOptionsTabProps {
  onError?: (msg: string) => void;
}

const CaseFieldOptionsTab: React.FC<CaseFieldOptionsTabProps> = ({ onError }) => {
  const [fieldIndex, setFieldIndex] = useState(0);
  const [options, setOptions] = useState<OptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<OptionRow | null>(null);
  const [form, setForm] = useState({ value: '', label: '', sort_order: 0, color: 'default' as string });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentField = FIELD_CONFIG[fieldIndex];

  const fetchOptions = async () => {
    if (!currentField) return;
    try {
      setLoading(true);
      setError(null);
      const list = await apiService.getCaseFieldOptionsByField(currentField.key);
      setOptions(list || []);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to load options';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [currentField?.key]);

  const handleOpenAdd = () => {
    setEditingOption(null);
    const nextOrder = options.length ? Math.max(...options.map((o) => o.sort_order), 0) + 10 : 10;
    setForm({ value: '', label: '', sort_order: nextOrder, color: 'default' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row: OptionRow) => {
    setEditingOption(row);
    setForm({ value: row.value, label: row.label, sort_order: row.sort_order, color: row.color || 'default' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmedValue = form.value.trim().toLowerCase().replace(/\s+/g, '_');
    const trimmedLabel = form.label.trim();
    if (!trimmedValue || !trimmedLabel) {
      setError('Value and label are required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const colorVal = form.color === 'default' ? undefined : form.color;
      let newList: { value: string; label: string; sort_order: number; color?: string }[];
      if (editingOption) {
        newList = options.map((o) =>
          o.id === editingOption.id
            ? { value: trimmedValue, label: trimmedLabel, sort_order: form.sort_order, color: colorVal }
            : { value: o.value, label: o.label, sort_order: o.sort_order, color: o.color || undefined }
        );
      } else {
        if (options.some((o) => o.value === trimmedValue)) {
          setError('An option with this value already exists');
          setSaving(false);
          return;
        }
        newList = [...options.map((o) => ({ value: o.value, label: o.label, sort_order: o.sort_order, color: o.color || undefined })), { value: trimmedValue, label: trimmedLabel, sort_order: form.sort_order, color: colorVal }];
      }
      await apiService.replaceCaseFieldOptions(currentField.key, newList);
      await fetchOptions();
      setDialogOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: OptionRow) => {
    if (!window.confirm(`Remove option "${row.label}"?`)) return;
    try {
      setError(null);
      const newList = options.filter((o) => o.id !== row.id).map((o) => ({ value: o.value, label: o.label, sort_order: o.sort_order, color: o.color || undefined }));
      await apiService.replaceCaseFieldOptions(currentField.key, newList);
      await fetchOptions();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 auto', minWidth: 0 }}>
          Manage dropdown options for case fields. These options are used in the Case create/edit forms and filters.
        </Typography>
        <Tabs
          value={fieldIndex}
          onChange={(_, v) => setFieldIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 36,
            flexShrink: 0,
            '& .MuiTabs-indicator': { height: 2, backgroundColor: 'primary.main' },
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              minHeight: 36,
              py: 0,
              px: 1.5,
              minWidth: 'auto',
              '&.Mui-selected': { fontWeight: 600, color: 'primary.main' },
            },
          }}
        >
          {FIELD_CONFIG.map((f, i) => (
            <Tab key={f.key} label={f.label} value={i} />
          ))}
        </Tabs>
      </Box>
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.5}>
          <Typography variant="subtitle1">{currentField?.label} options</Typography>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleOpenAdd}>
            Add option
          </Button>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Value</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell align="right">Sort order</TableCell>
                  <TableCell align="right" width={100}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {options.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }} color="text.secondary">
                      No options yet. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  options.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.value}</TableCell>
                      <TableCell>{row.label}</TableCell>
                      <TableCell>
                        <Chip
                          label={CHIP_COLORS.find((c) => c.value === (row.color || 'default'))?.label ?? (row.color || 'Default')}
                          size="small"
                          color={((row.color || 'default') as ChipColor)}
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                        />
                      </TableCell>
                      <TableCell align="right">{row.sort_order}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(row)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row)} title="Delete" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOption ? 'Edit option' : 'Add option'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Value (internal)"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            fullWidth
            margin="normal"
            disabled={!!editingOption}
            helperText={editingOption ? 'Value cannot be changed when editing.' : 'Stored in database; use lowercase, underscores for spaces.'}
          />
          <TextField
            label="Label (display)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Sort order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Color (for chips in tables)</InputLabel>
            <Select
              value={form.color}
              label="Color (for chips in tables)"
              onChange={(e) => setForm({ ...form, color: e.target.value })}
            >
              {CHIP_COLORS.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseFieldOptionsTab;
